import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chip, Details, Paragraph, Tag } from '@digdir/designsystemet-react';
import {
  AGREEMENTS,
  filterQuestionAreas,
  QUESTION_FILTERS,
  QuestionArea,
  QuestionFilter,
} from '../reportView';
import { ReportSection } from './ReportSection';
import classes from '../WorkEnvironmentReport.module.css';

type Props = {
  areas: QuestionArea[];
  filter: QuestionFilter;
  onFilterChange: (filter: QuestionFilter) => void;
  open: string[];
  onOpenChange: (open: string[]) => void;
};

const TICKS = ['1', '2', '3', '4', '5'];

const Spread = ({ question }: { question: QuestionArea['questions'][number] }) => (
  <div className={classes.spread} aria-hidden="true">
    <span className={classes.spreadTrack}>
      <span
        className={classes.spreadBand}
        data-agreement={question.agreement}
        style={{ left: question.bar.from, width: question.bar.width }}
      />
      <span className={classes.spreadMean} style={{ left: question.bar.mean }} />
    </span>
    <span className={`ds-paragraph ${classes.ticks}`} data-size="xs">
      {TICKS.map((tick) => (
        <span key={tick}>{tick}</span>
      ))}
    </span>
  </div>
);

export const QuestionsSection = ({ areas, filter, onFilterChange, open, onOpenChange }: Props): ReactElement => {
  const { t } = useTranslation();
  const shown = filterQuestionAreas(areas, filter);
  const allOpen = shown.every((area) => open.includes(area.key));
  const toggle = (key: string, isOpen: boolean) =>
    onOpenChange(isOpen ? [...open, key] : open.filter((item) => item !== key));

  return (
    <ReportSection section="questions" intro={t('workEnvironmentReport.questions.intro')}>
      <div className={classes.questionTools}>
        <div
          role="radiogroup"
          aria-label={t('workEnvironmentReport.questions.filter')}
          className={classes.chips}
          data-print="false"
        >
          {QUESTION_FILTERS.map((option) => (
            <Chip.Radio
              key={option}
              name="report-question-filter"
              checked={filter === option}
              onChange={() => onFilterChange(option)}
            >
              {t(`workEnvironmentReport.questions.filters.${option}`)}
            </Chip.Radio>
          ))}
        </div>
        <div className={classes.legendRow}>
          <ul aria-label={t('workEnvironmentReport.questions.legend')} className={classes.legend}>
            {AGREEMENTS.map((degree) => (
              <li key={degree} className="ds-paragraph" data-size="xs">
                <span className={classes.swatch} data-agreement={degree} aria-hidden="true" />
                {t(`workEnvironmentReport.agreement.${degree}`)}
              </li>
            ))}
          </ul>
          {shown.length > 0 && (
            <Button
              variant="tertiary"
              data-size="sm"
              data-print="false"
              onClick={() =>
                onOpenChange(allOpen ? [] : [...new Set([...open, ...shown.map((area) => area.key)])])
              }
            >
              {t(`workEnvironmentReport.questions.${allOpen ? 'closeAll' : 'openAll'}`)}
            </Button>
          )}
        </div>
      </div>
      {shown.length === 0 && <Paragraph data-size="sm">{t('workEnvironmentReport.questions.none')}</Paragraph>}
      <div className={classes.areas}>
        {shown.map((area) => (
          <Details
            key={area.key}
            id={`area-${area.key}`}
            className={classes.area}
            open={open.includes(area.key)}
            onToggle={(event) => toggle(area.key, (event.currentTarget as HTMLDetailsElement).open)}
          >
            <Details.Summary>
              <span className={classes.areaSummary}>
                <span className={classes.number} aria-hidden="true">
                  {area.number}
                </span>
                <span className={classes.areaTitle}>
                  <span className="ds-label">{area.name}</span>
                  <Tag data-size="sm" data-color={area.dimension === 'LOAD' ? 'brand1' : 'info'}>
                    {area.type}
                  </Tag>
                  <Tag data-size="sm" data-color={area.color}>
                    {area.level}
                  </Tag>
                  {area.wide && (
                    <span className={`ds-paragraph ${classes.wide}`} data-size="xs">
                      {t('workEnvironmentReport.questions.wide')}
                    </span>
                  )}
                </span>
                <span className={`ds-paragraph ${classes.areaScore}`} data-size="xs">
                  {area.score}
                </span>
              </span>
            </Details.Summary>
            <Details.Content>
              {area.description && (
                <Paragraph data-size="sm" className={`${classes.subtle} ${classes.areaDescription}`}>
                  {area.description}
                </Paragraph>
              )}
              <ul className={classes.questions}>
                {area.questions.map((question) => (
                  <li key={question.ref}>
                    <div>
                      <Paragraph data-size="sm">{question.text}</Paragraph>
                      {question.isIncident && (
                        <Tag data-size="sm" data-color="neutral" className={classes.incidentTag}>
                          {t('workEnvironmentReport.questions.incident')}
                        </Tag>
                      )}
                      <Paragraph data-size="xs" className={classes.questionFigures} data-agreement={question.agreement}>
                        {question.figures}
                      </Paragraph>
                    </div>
                    <Spread question={question} />
                  </li>
                ))}
              </ul>
            </Details.Content>
          </Details>
        ))}
      </div>
    </ReportSection>
  );
};
